import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useIntl } from "react-intl";

import { ActionsColumnFormatter } from "./column-formatters/ActionsColumnFormatter";
import { LevelColumnFormatter } from "./column-formatters/LevelColumnFormatter";
import dayjs from "dayjs";
import { initialFilter } from "../../helpers/queryParams";
import { TableComponent } from "../../../../../../components/TableComponent";
import { IAlert } from "../../models/IAlert";
import { alertServices } from "../../services/alertsServices";
import { changePageNumber } from "../../../../../../helpers/changePageNumber";
import { useAlert } from "../../helpers/useAlert";
import { AlertReview } from "../alert-review/AlertReview";
import { useAlertDimissed } from "../../helpers/useAlertDimissed";

interface IProps {
  hideAlertsModal: any;
  showAlertModal: boolean;
}
export function AlertsList({ hideAlertsModal, showAlertModal }: IProps) {

  const intl = useIntl();
  // Pagination
  const [totalCount, setTotalCount] = useState(0);
  const [entities, setEntities] = useState<IAlert[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [queryParams, setQueryParams] = useState(initialFilter);

  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [alert, setAlert] = useState<IAlert | null>(null);

  const handleCloseReviewModal = () => {
    setShowReviewModal(false);
    setAlert(null);
  };
  const handleShowReviewModal = (alert: IAlert) => {
    setShowReviewModal(true);
    setAlert(alert);
  };

  //Initialization methods
  useEffect(() => {
    if (queryParams.pageSize) {
      findNonDismissedAlerts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);
  // Listening NEW Alert Notification
  const newAlert = useAlert();
  useEffect(() => {
    let isActive = true;
    if (newAlert && !!isActive) {
      findNonDismissedAlerts();
    }
    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAlert]);
  const data = useAlertDimissed();
  useEffect(() => {
    let isActive = true;
    if (data && !!isActive) {
      refetchListAfterDimiss();
    }
    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data,showAlertModal]);

  const findNonDismissedAlerts = useCallback(async () => {
      setListLoading(true);
      const response = await alertServices.getNonDismissedAlerts(queryParams);
      const { totalCount, entities } = response?.data;
      setEntities(entities);
      setTotalCount(totalCount);
      setListLoading(false);
      if (totalCount === 0) {
        hideAlertsModal(false);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const dismissAlert = useCallback(
    async (id: number) => {
      await alertServices.dismissAlert(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryParams]
  );
  const markAllReviewed = useCallback(
    async () => {
      await alertServices.dismissAllAlerts();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryParams]
  );

  const refetchListAfterDimiss = () => {
    const newQueryParamsValue = changePageNumber(
      queryParams,
      totalCount,
      entities
    );
    if (newQueryParamsValue === false) {
      //The filters are maintained and the backend is called again.
      findNonDismissedAlerts();
    } else {
      // The filters remain the same but the page number changes.
      setQueryParams(newQueryParamsValue);
    }
  };

  const columns = [
    {
      dataField: "level",
      text: intl.formatMessage({ id: "ALERTS.LEVEL" }),
      formatter: LevelColumnFormatter,
    },
    {
      dataField: "source",
      text: intl.formatMessage({ id: "ALERTS.SOURCE" }),
      sort: false,
      style: {
        with: "20%",
      },
    },
    {
      dataField: "createdAt",
      text: intl.formatMessage({ id: "ALERTS.CREATEDAT" }),
      formatter: (cell: any, row: IAlert) => {
        return dayjs(row.createdAt).format("DD/MM/YYYY HH:mm:ss");
      },
      style: {
        width: "20%",
      },
    },
    {
      dataField: "message",
      text: intl.formatMessage({ id: "ALERTS.MESSAGE" }),
      style: {
        width: "60%",
      },
    },
    {
      dataField: "action",
      text: intl.formatMessage({ id: "ALERTS.ACTIONS" }),
      formatter: ActionsColumnFormatter,
      formatExtraData: {
        handleShowReviewModal,
        dismissAlert,
      },
      classes: "text-center",
      headerClasses: "text-center",
      style: {
        textAlign: "center",
        width: "20%",
      },
    },
  ];
  return (
    <Fragment>
      <div className="row">
        <div className="col-md-12 text-right margin-bottom-10-mobile">
          <button
            type="button"
            className="btn btn-light btn-hover-success"
            onClick={() => markAllReviewed()}
          >
            {intl.formatMessage({ id: "ALERTS.MARKASREVIEWED" })}
          </button>
        </div>
      </div>
      <div className="row">
        <div className="col-12 mt-6">
          <TableComponent
            table={{ entities, totalCount, columns }}
            setQueryParams={setQueryParams}
            queryParams={queryParams}
            listLoading={listLoading}
          />
        </div>
      </div>

      {/* Modals */}
      {showReviewModal && !!alert && (
        <AlertReview
          show={showReviewModal}
          onHide={handleCloseReviewModal}
          alert={alert}
          refetch={refetchListAfterDimiss}
        />
      )}
    </Fragment>
  );
}
